import type { ReactNode } from "react";

interface FieldProps {
  label: string;
  htmlFor?: string | undefined;
  /** Names the fix, never the fault. Rendered inline, under the input. */
  error?: string | null | undefined;
  hint?: ReactNode | undefined;
  children: ReactNode;
}

/**
 * Label + input + inline error.
 *
 * The error sits at the field rather than in a banner, because a banner makes the reader hunt for which field it
 * means. Wire it through `aria-describedby` at the call site so a screen reader announces the fix with the field.
 */
export function Field({ label, htmlFor, error, hint, children }: FieldProps) {
  return (
    <div className="mb-4 flex flex-col gap-2">
      <label htmlFor={htmlFor} className="text-[15px] font-bold text-text">
        {label}
      </label>
      {children}
      {error ? (
        <p
          id={htmlFor ? `${htmlFor}-error` : undefined}
          role="alert"
          className="m-0 text-[13px] text-negative"
        >
          {error}
        </p>
      ) : hint ? (
        <p className="m-0 text-[12.5px] text-text-muted">{hint}</p>
      ) : null}
    </div>
  );
}

/**
 * Shared box so every input in the flow looks the same: v2's bold 2px outline, 10px corners, soft crimson focus
 * ring. Single-line inputs and selects are at least 52px tall; a textarea grows from its rows.
 */
export const inputShell =
  "w-full min-h-[52px] rounded-[var(--radius-control)] border-2 border-field-border bg-surface px-3.5 py-3 text-[16px] font-medium text-text placeholder:font-normal placeholder:text-[#b9a894] transition-[border-color,box-shadow] duration-200 focus:border-accent focus:shadow-[0_0_0_4px_var(--focus-ring)] focus:outline-none";
